/**
 * iOS Safari Audio Helper V2 - BUILD 52.4
 * Handles iOS-specific audio unlock and playback issues
 * 
 * iOS Safari requires user gesture to:
 * 1. Resume AudioContext from 'suspended' state
 * 2. Start HTMLAudioElement playback
 * 
 * BUILD 52.4 UPDATES:
 * - Enhanced diagnostic logging for TestFlight debugging
 * - AVAudioSession state logging via native bridge
 * - Background audio monitoring
 * 
 * This helper provides:
 * - Silent audio unlock on first tap
 * - Safe play() wrapper with instrumentation
 * - AudioContext resume helper
 */

// Silent audio (0.1s of silence) - base64 encoded MP3
// This is used to "unlock" iOS audio on first user gesture
const SILENT_AUDIO_BASE64 = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRwmHAAAAAAD/+1DEAAAGAAGn9AAAIAAANIAAAAQAAAGkAAAAIAAAAaQAAAAgAAA0gAAAAEludHJvAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQxAADwAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVU=';

// Track unlock state
let isAudioUnlocked = false;
let unlockAttempted = false;

/**
 * Check if current browser is iOS Safari
 * @returns {boolean}
 */
export function isIOSSafari() {
  if (typeof window === 'undefined') return false;
  
  const ua = window.navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /safari/.test(ua) && !/chrome|crios|fxios|edgios/.test(ua);
  
  return isIOS && isSafari;
}

/**
 * Check if current device is iOS (any browser)
 * @returns {boolean}
 */
export function isIOS() {
  if (typeof window === 'undefined') return false;
  
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Get current audio unlock status
 * @returns {boolean}
 */
export function isUnlocked() {
  return isAudioUnlocked;
}

/**
 * Resume an AudioContext if suspended
 * @param {AudioContext} audioContext - The AudioContext to resume
 * @returns {Promise<string>} - Final state of AudioContext
 */
export async function resumeAudioContext(audioContext) {
  if (!audioContext) {
    console.warn('[iOS Audio] No AudioContext provided');
    return 'no-context';
  }
  
  const stateBefore = audioContext.state;
  
  if (stateBefore === 'suspended') {
    try {
      await audioContext.resume();
      console.log(`[iOS Audio] ✅ AudioContext resumed: ${stateBefore} → ${audioContext.state}`);
    } catch (error) {
      console.error('[iOS Audio] ❌ AudioContext resume failed:', error);
    }
  }
  
  return audioContext.state;
}

/**
 * Unlock audio on first user tap (iOS Safari requirement)
 * MUST be called synchronously within a user gesture handler
 * @param {AudioContext} audioContext - Optional AudioContext to resume
 * @returns {Promise<boolean>} - Whether unlock was successful
 */
export async function unlockAudioOnFirstTap(audioContext = null) {
  // Only attempt once
  if (unlockAttempted) {
    console.log('[iOS Audio] Unlock already attempted, skipping');
    return isAudioUnlocked;
  }
  
  unlockAttempted = true;
  
  if (!isIOS()) {
    console.log('[iOS Audio] Not iOS, skipping unlock');
    isAudioUnlocked = true;
    return true;
  }
  
  console.log('[iOS Audio] 🔓 Attempting audio unlock on user gesture...');
  
  try {
    // Step 1: Play silent audio to unlock HTMLAudioElement
    const silentAudio = new Audio(SILENT_AUDIO_BASE64);
    silentAudio.volume = 0.01; // Nearly silent
    silentAudio.muted = false;
    
    // CRITICAL: play() must be called synchronously in gesture handler
    const playPromise = silentAudio.play();
    
    if (playPromise !== undefined) {
      await playPromise;
      console.log('[iOS Audio] ✅ Silent audio played successfully');
    }
    
    // Clean up
    silentAudio.pause();
    silentAudio.src = '';
    
    // Step 2: Resume AudioContext if provided
    if (audioContext) {
      await resumeAudioContext(audioContext);
    }
    
    isAudioUnlocked = true;
    console.log('[iOS Audio] ✅ Audio unlocked successfully (context: ' + 
      (audioContext ? audioContext.state : 'none') + ')');
    
    // Dispatch custom event for other components to know
    window.dispatchEvent(new CustomEvent('iosAudioUnlocked', { 
      detail: { unlocked: true, contextState: audioContext?.state } 
    }));
    
    return true;
    
  } catch (error) {
    console.error('[iOS Audio] ❌ Unlock failed:', {
      error: error.name,
      message: error.message,
      contextState: audioContext?.state
    });
    
    // Still mark as attempted to avoid infinite retries
    isAudioUnlocked = false;
    return false;
  }
}

/**
 * Play audio safely with iOS instrumentation
 * Wraps HTMLAudioElement.play() with proper error handling and logging
 * @param {HTMLAudioElement} audioElement - The audio element to play
 * @param {string} source - Description of the audio source (for logging)
 * @returns {Promise<boolean>} - Whether play was successful
 */
export async function playAudioSafely(audioElement, source = 'unknown') {
  if (!audioElement) {
    console.error('[iOS Audio] playAudioSafely: No audio element provided');
    return false;
  }
  
  const logContext = {
    source,
    src: audioElement.src?.substring(0, 100),
    readyState: audioElement.readyState,
    networkState: audioElement.networkState,
    paused: audioElement.paused,
    isUnlocked: isAudioUnlocked
  };
  
  // Warn if audio not unlocked on iOS
  if (isIOS() && !isAudioUnlocked) {
    console.warn('[iOS Audio] ⚠️ Attempting play before unlock:', logContext);
  }
  
  try {
    const playPromise = audioElement.play();
    
    if (playPromise !== undefined) {
      await playPromise;
    }
    
    console.log(`[iOS Audio] ✅ play() success: ${source}`);
    return true;
    
  } catch (error) {
    // Detailed error logging for iOS debugging
    console.error('[iOS Audio] ❌ play() rejected:', {
      ...logContext,
      error: error.name,
      message: error.message
    });
    
    // Specific handling for common iOS errors
    if (error.name === 'NotAllowedError') {
      console.error('[iOS Audio] 💡 NotAllowedError: User gesture required. Call unlockAudioOnFirstTap() on user interaction.');
    } else if (error.name === 'AbortError') {
      console.log('[iOS Audio] ℹ️ AbortError: Play was interrupted (likely by another play call)');
    } else if (error.name === 'NotSupportedError') {
      console.error('[iOS Audio] 💡 NotSupportedError: Audio format may not be supported');
    }
    
    return false;
  }
}

/**
 * Create and play a BufferSource safely (for Web Audio API)
 * @param {AudioContext} audioContext - The AudioContext
 * @param {AudioBuffer} buffer - The audio buffer to play
 * @param {AudioNode} destination - Where to connect the source
 * @param {boolean} loop - Whether to loop
 * @returns {AudioBufferSourceNode|null} - The playing source node or null on failure
 */
export function playBufferSafely(audioContext, buffer, destination, loop = false) {
  if (!audioContext || !buffer || !destination) {
    console.error('[iOS Audio] playBufferSafely: Missing required parameters');
    return null;
  }
  
  // Ensure context is running
  if (audioContext.state === 'suspended') {
    console.warn('[iOS Audio] ⚠️ AudioContext suspended, attempting resume...');
    audioContext.resume();
  }
  
  try {
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.loop = loop;
    source.connect(destination);
    source.start(0);
    
    console.log(`[iOS Audio] ✅ BufferSource started (loop=${loop}, duration=${buffer.duration.toFixed(1)}s)`);
    return source;
    
  } catch (error) {
    console.error('[iOS Audio] ❌ BufferSource failed:', {
      error: error.name,
      message: error.message,
      contextState: audioContext.state
    });
    return null;
  }
}

/**
 * Install global unlock listener (call once in App.js)
 * @param {AudioContext} audioContext - Optional AudioContext to resume on unlock
 */
export function installGlobalUnlockListener(audioContext = null) {
  if (typeof window === 'undefined') return;
  
  // Skip if not iOS
  if (!isIOS()) {
    console.log('[iOS Audio] Not iOS, skipping global unlock listener');
    isAudioUnlocked = true;
    return;
  }
  
  console.log('[iOS Audio] 📱 Installing global unlock listener for iOS...');
  
  const unlockHandler = async (event) => {
    console.log(`[iOS Audio] 🖱️ User gesture detected: ${event.type}`);
    await unlockAudioOnFirstTap(audioContext);
  };
  
  // Listen for first user interaction
  // { once: true } ensures this only fires once
  // { capture: true } ensures we catch it before any stopPropagation
  const options = { once: true, capture: true, passive: true };
  
  window.addEventListener('touchstart', unlockHandler, options);
  window.addEventListener('touchend', unlockHandler, options);
  window.addEventListener('click', unlockHandler, options);
  window.addEventListener('pointerdown', unlockHandler, options);
  
  console.log('[iOS Audio] ✅ Global unlock listeners installed');
}

/**
 * Force unlock attempt (for manual retry)
 * @param {AudioContext} audioContext - Optional AudioContext to resume
 */
export async function forceUnlock(audioContext = null) {
  console.log('[iOS Audio] 🔄 Force unlock requested');
  unlockAttempted = false;
  isAudioUnlocked = false;
  return await unlockAudioOnFirstTap(audioContext);
}

// Export default object for convenience
export default {
  isIOSSafari,
  isIOS,
  isUnlocked,
  resumeAudioContext,
  unlockAudioOnFirstTap,
  playAudioSafely,
  playBufferSafely,
  installGlobalUnlockListener,
  forceUnlock,
  logAudioDiagnostics
};

/**
 * BUILD 52.4: Enhanced diagnostic logging for TestFlight debugging
 * Logs comprehensive audio state information for debugging screen-lock audio issues
 * @param {HTMLAudioElement} audioElement - Optional audio element to inspect
 * @param {AudioContext} audioContext - Optional AudioContext to inspect
 * @param {string} context - Description of when this is called (e.g., "beforePlay", "afterPause")
 */
export function logAudioDiagnostics(audioElement = null, audioContext = null, context = 'manual') {
  console.log('═══════════════════════════════════════════════');
  console.log(`[iOS][Audio] 🔍 DIAGNOSTIC LOG - ${context}`);
  console.log('═══════════════════════════════════════════════');
  
  // Platform info
  console.log(`[iOS][Audio] Platform: ${isIOS() ? 'iOS' : 'Non-iOS'}`);
  console.log(`[iOS][Audio] Safari: ${isIOSSafari() ? 'Yes' : 'No'}`);
  console.log(`[iOS][Audio] Audio unlocked: ${isAudioUnlocked}`);
  console.log(`[iOS][Audio] Unlock attempted: ${unlockAttempted}`);
  
  // AudioContext state
  if (audioContext) {
    console.log(`[iOS][Audio] AudioContext state: ${audioContext.state}`);
    console.log(`[iOS][Audio] AudioContext sampleRate: ${audioContext.sampleRate}`);
    console.log(`[iOS][Audio] AudioContext baseLatency: ${audioContext.baseLatency || 'N/A'}`);
  } else {
    console.log('[iOS][Audio] AudioContext: not provided');
  }
  
  // HTMLAudioElement state
  if (audioElement) {
    console.log(`[iOS][Audio] element.paused: ${audioElement.paused}`);
    console.log(`[iOS][Audio] element.currentTime: ${audioElement.currentTime?.toFixed(3)}s`);
    console.log(`[iOS][Audio] element.duration: ${audioElement.duration?.toFixed(1) || 'unknown'}s`);
    console.log(`[iOS][Audio] element.readyState: ${audioElement.readyState} (4=HAVE_ENOUGH_DATA)`);
    console.log(`[iOS][Audio] element.networkState: ${audioElement.networkState}`);
    console.log(`[iOS][Audio] element.volume: ${audioElement.volume}`);
    console.log(`[iOS][Audio] element.muted: ${audioElement.muted}`);
    console.log(`[iOS][Audio] element.loop: ${audioElement.loop}`);
    console.log(`[iOS][Audio] element.src: ${audioElement.src?.substring(0, 80)}...`);
  } else {
    console.log('[iOS][Audio] element: not provided');
  }
  
  // Global audio references (Discover)
  if (window.discoverAmbientAudio) {
    const bed = window.discoverAmbientAudio;
    console.log(`[iOS][Audio] Discover bed.paused: ${bed.paused}`);
    console.log(`[iOS][Audio] Discover bed.currentTime: ${bed.currentTime?.toFixed(3)}s`);
  }
  if (window.discoverVoiceAudio) {
    const voice = window.discoverVoiceAudio;
    console.log(`[iOS][Audio] Discover voice.paused: ${voice.paused}`);
    console.log(`[iOS][Audio] Discover voice.currentTime: ${voice.currentTime?.toFixed(3)}s`);
  }
  
  console.log('═══════════════════════════════════════════════');
}

/**
 * BUILD 52.4: Monitor audio playback state changes
 * Attaches event listeners to log when audio pauses unexpectedly (e.g., screen lock)
 * @param {HTMLAudioElement} audioElement - The audio element to monitor
 * @param {string} label - Label for logging (e.g., "Discover-Bed", "Radio")
 */
export function monitorAudioPlayback(audioElement, label = 'Audio') {
  if (!audioElement) return;
  
  const prefix = `[iOS][Audio][${label}]`;
  
  audioElement.addEventListener('play', () => {
    console.log(`${prefix} ▶️ EVENT: play`);
  });
  
  audioElement.addEventListener('pause', () => {
    console.log(`${prefix} ⏸️ EVENT: pause (currentTime=${audioElement.currentTime?.toFixed(3)}s)`);
    // This is critical - if pause happens without user action, it's likely a screen lock issue
    console.log(`${prefix} ⚠️ If unexpected pause, check AVAudioSession category in native logs`);
  });
  
  audioElement.addEventListener('ended', () => {
    console.log(`${prefix} ⏹️ EVENT: ended`);
  });
  
  audioElement.addEventListener('error', (e) => {
    console.error(`${prefix} ❌ EVENT: error`, audioElement.error?.code, audioElement.error?.message);
  });
  
  audioElement.addEventListener('waiting', () => {
    console.log(`${prefix} ⏳ EVENT: waiting (buffering)`);
  });
  
  audioElement.addEventListener('playing', () => {
    console.log(`${prefix} ✅ EVENT: playing (actually producing sound)`);
  });
  
  console.log(`${prefix} 📡 Playback monitoring attached`);
}
